import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { Router } from '@angular/router'

import { provideLoadingBar } from '@ngx-loading-bar/core'
import { provideLoadingBarRouter } from '@ngx-loading-bar/router'
import * as Sentry from '@sentry/angular'
import { MessageService } from 'primeng/api'
import { TooltipStyle } from 'primeng/tooltip'

import { ApiModule } from '@ti/core/api/api.module'
import { AuthModule } from '@ti/core/auth/auth.module'
import { TspProfile } from '@ti/core/auth/services'
import { provideFirebaseApp } from '@ti/core/auth/utils/provide-fb-app'
import { AppCommonModule } from '@ti/core/common/common.module'
import { GlobalErrorHandler } from '@ti/core/common/services'
import { provideCustomRouteReuseStrategy } from '@ti/core/common/strategies'
import { USERS_DIRECTORY } from '@ti/core/contracts/users-directory'
import { providePrimeNgPreset } from '@ti/core/themes/services/primeng-preset'
import {
  FleetI18NConfigProvider,
  I18NConfigProvider,
  provideLanguageInitializer,
  provideTranslationsLoader,
} from '@ti/core/ui-kit/i18n'
import { I18nModule } from '@ti/core/ui-kit/i18n/i18n.module'
import { DIALOG_SERVICE_PROVIDER, provideUiKitGlobals } from '@ti/core/ui-kit/providers/globals'
import { provideBookmarksFacade } from '@ti/fleet/bookmarks/services'
import { FleetUsersStore } from '@ti/fleet/settings/services/fleet-users.store'
import { provideFleetUserManager } from '@ti/fleet/settings/services/user.manager'
import { provideFleetAlerts } from '@ti/shared/alerts/utils'
import { provideChatFacade } from '@ti/shared/chat/services'
import { FleetMapLayersProvider } from '@ti/shared/fleet/services/map-layers.provider'
import { provideFleetLocationsSearch } from '@ti/shared/locations'
import { MapLayersProvider } from '@ti/shared/map/services/map-layers.provider'
import { UserModule } from '@ti/shared/user/user.module'

import { AppRoutingModule } from './app-routing.module'
import { provideFleetAudit } from './audit'
import { AUTH_PROVIDERS, provideFleetLanguageService } from './services'

const NG_MODULES = [
  // Angular
  BrowserModule,
]
const APP_MODULES = [
  ApiModule,
  AppCommonModule,
  AppRoutingModule,
  AuthModule,
  I18nModule,
  UserModule,
]
@NgModule({
  imports: [...NG_MODULES, ...APP_MODULES],
  providers: [
    TspProfile,
    TooltipStyle,
    DIALOG_SERVICE_PROVIDER,
    MessageService,
    provideAnimationsAsync('animations'),
    provideTranslationsLoader(['fleet', 'shared']),
    provideLanguageInitializer(),
    { provide: ErrorHandler, useFactory: () => new GlobalErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    // Init loading bar
    provideLoadingBar({}),
    provideLoadingBarRouter(),
    // Init sentry service
    provideAppInitializer(() => void inject(Sentry.TraceService)),
    provideFirebaseApp(),
    ...AUTH_PROVIDERS,
    provideFleetLanguageService(),
    { provide: I18NConfigProvider, useClass: FleetI18NConfigProvider },
    provideCustomRouteReuseStrategy(),
    provideFleetAlerts(),
    provideFleetAudit(),
    provideBookmarksFacade(),
    { provide: USERS_DIRECTORY, useFactory: () => inject(FleetUsersStore).view(undefined) },
    provideChatFacade(),
    provideFleetLocationsSearch(),
    { provide: MapLayersProvider, useClass: FleetMapLayersProvider },
    providePrimeNgPreset(),
    provideFleetUserManager(),
    provideUiKitGlobals(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class FleetAppShellModule {}
